export default {
    /**
     * Check if the new dropdown news filter form is present
     */
    when() {
        return document.querySelectorAll('#news-filter-form').length > 0;
    },

    mounted() {
        const section = document.querySelector('#news');
        const form = document.querySelector('#news-filter-form');
        if (!section || !form) return;

        const postId = section.getAttribute('data-post-id') || '';
        const gridEl = section.querySelector('[data-news-grid]');
        const paginationEl = section.querySelector('[data-news-pagination]');
        const badgesWrapperEl = section.querySelector('[data-badges-wrapper]');
        const badgesListEl = section.querySelector('[data-badges-list]');
        const resetLink = section.querySelector('[data-reset-filters]');
        const dropdowns = form.querySelectorAll('.news-filter-dropdown');

        // Helper: Close all open dropdowns
        const closeAllDropdowns = () => {
            dropdowns.forEach((dropdown) => {
                const trigger = dropdown.querySelector('.news-filter-dropdown__trigger');
                const menu = dropdown.querySelector('.news-filter-dropdown__menu');
                const chevron = trigger ? trigger.querySelector('.fa-chevron-down') : null;

                if (menu) menu.classList.add('hidden');
                if (trigger) trigger.setAttribute('aria-expanded', 'false');
                if (chevron) chevron.classList.remove('rotate-180');
            });
        };

        // 1. Dropdown Toggle & Dynamic Label Update Logic
        dropdowns.forEach((dropdown) => {
            const trigger = dropdown.querySelector('.news-filter-dropdown__trigger');
            const triggerSpan = trigger ? trigger.querySelector('span') : null;
            const menu = dropdown.querySelector('.news-filter-dropdown__menu');
            const chevron = trigger ? trigger.querySelector('.fa-chevron-down') : null;
            const defaultLabel = trigger ? trigger.getAttribute('data-default-label') : '';

            if (!trigger || !menu) return;

            const updateTriggerLabel = () => {
                const checkedBoxes = menu.querySelectorAll('input[type="checkbox"]:checked');
                if (checkedBoxes.length > 0) {
                    const labels = Array.from(checkedBoxes).map((cb) => {
                        const labelSpan = cb.closest('label').querySelector('span');
                        return labelSpan ? labelSpan.textContent.trim() : '';
                    }).filter(Boolean);

                    if (triggerSpan) {
                        triggerSpan.textContent = labels.join(', ');
                    }
                } else if (triggerSpan && defaultLabel) {
                    triggerSpan.textContent = defaultLabel;
                }
            };

            menu.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
                checkbox.addEventListener('change', updateTriggerLabel);
            });

            trigger.addEventListener('click', (event) => {
                event.stopPropagation();
                const isOpen = !menu.classList.contains('hidden');

                closeAllDropdowns();

                if (!isOpen) {
                    menu.classList.remove('hidden');
                    trigger.setAttribute('aria-expanded', 'true');
                    if (chevron) chevron.classList.add('rotate-180');
                }
            });

            menu.addEventListener('click', (event) => {
                event.stopPropagation();
            });
        });

        document.addEventListener('click', () => {
            closeAllDropdowns();
        });

        // 2. Loading state helper
        const setLoading = (isLoading) => {
            section.classList.toggle('is-loading', isLoading);
            if (gridEl) gridEl.classList.toggle('opacity-50', isLoading);
            if (gridEl) gridEl.classList.toggle('pointer-events-none', isLoading);
        };

        // 3. Build the filter params from the checked checkboxes in the form
        const buildFilterParams = () => {
            const params = new URLSearchParams();

            form.querySelectorAll('input[type="checkbox"]:checked').forEach((checkbox) => {
                params.append(checkbox.name, checkbox.value);
            });

            return params;
        };

        // 4. Keep the visible URL in sync with the current filters/page, without reloading
        const syncUrl = (postedParams) => {
            const visibleParams = new URLSearchParams();

            for (const [key, val] of postedParams.entries()) {
                if (key === 'action' || key === '__wpnonce' || key === 'post_id') continue;
                if (key === 'paged' && val === '1') continue;
                visibleParams.append(key, val);
            }

            const url = new URL(window.location.href);
            url.search = visibleParams.toString();
            url.hash = 'news';
            window.history.pushState({}, '', url.toString());
        };

        // 5. Render the JSON response returned by the `fetch_news` AJAX action
        const renderResults = (json) => {
            if (gridEl && typeof json.grid === 'string') gridEl.innerHTML = json.grid;
            if (paginationEl && typeof json.pagination === 'string') paginationEl.innerHTML = json.pagination;
            if (badgesListEl && typeof json.badges === 'string') badgesListEl.innerHTML = json.badges;
            if (badgesWrapperEl) badgesWrapperEl.classList.toggle('hidden', !json.hasFilters);
        };

        // 6. Fetch news for a given page, using the currently checked filters
        const fetchNews = (pageNumber = 1) => {
            if (!window.__app || !window.__app.ajax_url) return Promise.resolve();

            const params = buildFilterParams();
            params.set('paged', String(pageNumber));
            params.set('post_id', postId);
            params.set('action', 'fetch_news');
            params.set('__wpnonce', window.__app.nonce || '');

            setLoading(true);

            return fetch(window.__app.ajax_url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: params.toString(),
            })
                .then((response) => response.json())
                .then((json) => {
                    if (!json || !json.success) return;
                    renderResults(json);
                    syncUrl(params);
                })
                .catch((error) => {
                    console.error('[NewsDropdownFilters] Unable to fetch news', error);
                })
                .finally(() => setLoading(false));
        };

        // 7. Precise Active Filter Badge Removal ("×" buttons) - delegated, badges are re-rendered on every fetch
        if (badgesListEl) {
            badgesListEl.addEventListener('click', (event) => {
                const removeButton = event.target.closest('[data-remove-filter]');
                if (!removeButton) return;
                event.preventDefault();

                const param = removeButton.getAttribute('data-param');
                const value = removeButton.getAttribute('data-value');
                const checkbox = param && value
                    ? form.querySelector(`input[name="${param}[]"][value="${CSS.escape(value)}"]`)
                    : null;

                if (checkbox) {
                    checkbox.checked = false;
                    checkbox.dispatchEvent(new Event('change'));
                }

                fetchNews(1);
            });
        }

        // 8. Reset filters
        if (resetLink) {
            resetLink.addEventListener('click', (event) => {
                event.preventDefault();

                form.querySelectorAll('input[type="checkbox"]:checked').forEach((checkbox) => {
                    checkbox.checked = false;
                    checkbox.dispatchEvent(new Event('change'));
                });

                closeAllDropdowns();
                fetchNews(1);
            });
        }

        // 9. Pagination links - delegated, pagination markup is re-rendered on every fetch
        const getPagedFromHref = (href) => {
            try {
                const url = new URL(href, window.location.origin);
                const queryPaged = url.searchParams.get('paged');
                if (queryPaged) return parseInt(queryPaged, 10) || 1;

                const match = url.pathname.match(/\/page\/(\d+)/);
                if (match) return parseInt(match[1], 10) || 1;
            } catch (error) {
                // ignore malformed URLs and fall back to page 1
            }

            return 1;
        };

        if (paginationEl) {
            paginationEl.addEventListener('click', (event) => {
                const link = event.target.closest('a');
                if (!link) return;
                event.preventDefault();

                fetchNews(getPagedFromHref(link.getAttribute('href')));
                section.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
        }

        // 10. Helper: Check if form inputs match the current URL parameters
        const isFormUnchanged = () => {
            const currentParams = new URLSearchParams(window.location.search);
            const formData = new FormData(form);

            const filterKeys = ['in_category[]', 'in_expertise[]', 'in_country[]', 'in_year[]'];
            const formMap = {};
            const urlMap = {};

            filterKeys.forEach((key) => {
                formMap[key] = [];
                urlMap[key] = [];
            });

            for (const [key, val] of formData.entries()) {
                if (filterKeys.includes(key)) {
                    formMap[key].push(String(val));
                }
            }

            for (const [key, val] of currentParams.entries()) {
                const normalizedKey = key.endsWith('[]') ? key : `${key}[]`;
                if (filterKeys.includes(normalizedKey)) {
                    urlMap[normalizedKey].push(String(val));
                }
            }

            for (const key of filterKeys) {
                const formVals = formMap[key].sort();
                const urlVals = urlMap[key].sort();

                if (formVals.length !== urlVals.length) return false;
                for (let i = 0; i < formVals.length; i++) {
                    if (formVals[i] !== urlVals[i]) return false;
                }
            }

            return true;
        };

        // 11. Form Submission via the Primary Button Component
        const submitWrapper = form.querySelector('.news-filter-submit');
        if (submitWrapper) {
            const submitLink = submitWrapper.querySelector('a');
            if (submitLink) {
                submitLink.addEventListener('click', (event) => {
                    event.preventDefault();
                    closeAllDropdowns();

                    // If filters are identical to the URL state, skip the fetch
                    if (isFormUnchanged()) return;

                    fetchNews(1);
                });
            }
        }

        // 12. Support browser back/forward navigation between filtered/paginated states
        window.addEventListener('popstate', () => {
            const params = new URLSearchParams(window.location.search);

            form.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
                checkbox.checked = params.getAll(checkbox.name).includes(checkbox.value);
                checkbox.dispatchEvent(new Event('change'));
            });

            const paged = parseInt(params.get('paged'), 10) || 1;
            fetchNews(paged);
        });
    },
};
